import type { AdjustmentVersion } from '@/react-query/useGetSharpenedImages';
import { t } from '@lingui/core/macro';

/** Single HD slot: label (e.g. "HD1") and its saved default filter value. */
export interface DefaultFilterSlot {
  label: string;
  value: AdjustmentVersion | null;
}

/** All info needed to show "current default" badges and build filter options. */
export interface DefaultFilterInfo {
  /** Default for the currently selected HD (used for single-HD badge and initial selection). */
  current: AdjustmentVersion | null;
  /** All HD slots; when showMultipleHdLabels is true, badges show which slot(s) use this filter. */
  slots: DefaultFilterSlot[];
  /** When true, show per-HD-version labels (e.g. "Current HD1 Default") instead of generic "Current Default Filter". */
  showMultipleHdLabels: boolean;
}

/** Preference keys and labels for each HD slot. Single source of truth for "what HDs exist". */
const HD_SLOT_CONFIG = [
  { preferenceKey: 'adjustmentVersion' as const, label: 'HD1' },
  { preferenceKey: 'adjustmentVersionHD2' as const, label: 'HD2' },
];

export type PreferencesForDefaultFilter = {
  adjustmentVersion?: string;
  adjustmentVersionHD2?: string;
  enableSharpeningHd2Selection?: boolean;
};

/**
 * Build default filter info from preferences and current HD selection.
 * Centralizes preference keys and HD labels.
 */
export function getDefaultFilterInfo(
  preferences: PreferencesForDefaultFilter | undefined,
  isHD1Selected: boolean,
): DefaultFilterInfo {
  const slots: DefaultFilterSlot[] = HD_SLOT_CONFIG.map(
    ({ preferenceKey, label }) => ({
      label,
      value: (preferences?.[preferenceKey] as AdjustmentVersion) ?? null,
    }),
  );
  const currentIndex = isHD1Selected ? 0 : 1;
  const current = slots[currentIndex]?.value ?? null;
  const showMultipleHdLabels = Boolean(preferences?.enableSharpeningHd2Selection);
  return { current, slots, showMultipleHdLabels };
}

/** Options for the filter select, including deprecated defaults not in the base list. */
export function getFilterOptions(
  baseOptions: AdjustmentVersion[],
  defaultFilterInfo: DefaultFilterInfo,
): (AdjustmentVersion | null)[] {
  const { current, slots, showMultipleHdLabels } = defaultFilterInfo;
  const defaultValues = showMultipleHdLabels
    ? slots
        .map((s) => s.value)
        .filter(
          (v): v is AdjustmentVersion =>
            v != null && !baseOptions.includes(v),
        )
    : current && !baseOptions.includes(current)
      ? [current]
      : [];
  const unique = [...new Set(defaultValues)];
  return [null, ...unique, ...baseOptions];
}

export const adjustmentVersionLabel = (filter: AdjustmentVersion | null) => {
  const adjustmentVersionDescriptions: Record<
    AdjustmentVersion,
    { label: string; description: string }
  > = {
    '6.0.0.enhanced': {
      label: t`Crisp 1`,
      description: t`Boosts contrast and edge clarity for a sharper look.`,
    },
    '7.1.0.enhanced': {
      label: t`Smooth 1`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '7.2.0.enhanced': {
      label: t`Balanced 2`,
      description: t`Minimal enhancement that preserves tonal balance.`,
    },
    '7.3.0.enhanced': {
      label: t`Crisp 2`,
      description: t`Boosts contrast and edge clarity for a sharper look.`,
    },
    '7.4.0.enhanced': {
      label: t`Crisp 3`,
      description: t`Boosts contrast and edge clarity for a sharper look.`,
    },
    '7.4.1.enhanced': {
      label: t`Crisp 4`,
      description: t`Boosts contrast and edge clarity for a sharper look.`,
    },
    '7.5.0.enhanced': {
      label: t`Smooth 2`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '7.5.1.enhanced': {
      label: t`Smooth 2.1`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '7.5.2.enhanced': {
      label: t`Smooth 2.2`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '7.5.3.enhanced': {
      label: t`Smooth 3`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '7.6.0.enhanced': {
      label: t`Adapt Endo`,
      description: t`Sharpening similar to Carestream\'s \'Endo 3\' preset`,
    },
    '8.0.0.enhanced': {
      label: t`Smooth 4`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
    '8.0.1.enhanced': {
      label: t`Smooth 5`,
      description: t`Reduces noise and harsh contrast for a cleaner appearance.`,
    },
  };

  if (!filter) {
    return {
      label: t`Original`,
      description: t`Unprocessed baseline view with no enhancement applied.`,
    };
  }
  return (
    adjustmentVersionDescriptions[filter] || {
      label: filter,
      description: `Version ${filter}`,
    }
  );
};
